import type { ReactNode } from "react";
import { IconAlertTriangle } from "@tabler/icons-react";

import { Dialog } from "./dialog";
import { IconPill } from "./icon-pill";

interface ConfirmDialogProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** Called with `false` when the dialog should close (cancel, backdrop, or close icon). */
  onOpenChange: (open: boolean) => void;
  /** Invoked when the user confirms the action. */
  onConfirm: () => void;
  title: string;
  /** Supporting copy explaining the consequence of confirming. */
  description?: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmText?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelText?: string;
  /** Shows the confirm button as busy and locks the dialog open while the request is in flight. */
  loading?: boolean;
  /** When true, renders the destructive (warning) icon. Defaults to true. */
  destructive?: boolean;
  /** Override the title icon. Falls back to a warning triangle for destructive dialogs. */
  icon?: ReactNode;
}

/**
 * Reusable confirmation dialog for destructive or irreversible actions.
 * Wraps the shared `Dialog` primitive so every confirm flow looks and behaves
 * the same across the app. Pass `loading` to keep it open while the action runs.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  destructive = true,
  icon,
}: ConfirmDialogProps) {
  const titleIcon =
    icon ??
    (destructive ? (
      <IconPill tone="outline" shape="circle" size={48}>
        <IconAlertTriangle size={24} className="text-accentError" />
      </IconPill>
    ) : undefined);

  return (
    <Dialog
      open={open}
      setOpen={onOpenChange}
      title={title}
      text={description}
      titleIcon={titleIcon}
      titleSize="lg"
      showCloseButton
      buttonText={loading ? "Working…" : confirmText}
      cancelText={cancelText}
      onConfirm={onConfirm}
      busy={loading}
    />
  );
}
